import * as React from "react";
import { QueryRoute } from "bernie-core";
import { Layer } from "bernie-view";
import { useLocalization } from "@shared-ui/localization-context";
import { inject, observer } from "mobx-react";
import LazyLoad from "react-lazyload";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import { UitkButton } from "uitk-react-button";
import { UitkScrim, UitkScrimType } from "uitk-react-scrim";
import { UitkIcon } from "uitk-react-icons";
import { UitkToolbar } from "uitk-react-toolbar";
import { UitkDialogAnimationType, UitkDialogContent, UitkFullscreenDialog } from "uitk-react-dialog";
import {
  UitkMediaGalleryImageDialog,
  UitkMediaGalleryImageView,
  UitkMediaGalleryThumbnailView,
} from "uitk-react-media-gallery";
import { AttributionProps, GalleryImage, GalleryMosaicProps, GalleryMosaicThumbProps } from "../typings";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { Action, FlexTrackingInfo, sendImmediateTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import { AttributionText } from "components/shared/AttributionText/AttributionText";
import { getFullScreenImages } from "components/utility/GalleryImageUtil";
import { UitkPlaceholderCard } from "uitk-react-cards";

const GalleryMosaic = inject("routeStore")((props: GalleryMosaicProps) => {
  const { analytics, images, routeStore } = props;
  const galleryImages = getFullScreenImages(images);
  const { formatText } = useLocalization();
  const [currentMediaGalleryIndex, setCurrentMediaGalleryIndex] = React.useState(0);

  const setDialogGalleryIndex = (index: number) => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryMosaic",
      rfrr: "mosaic.gallery-nav.btn",
      action: Action.CLICK,
      linkName: "Navigating mosaic gallery viewer",
    };
    sendImmediateTrackEvent(trackingInfo, analytics);
    setCurrentMediaGalleryIndex(index);
  };

  const openMediaGallery = (index: number) => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryMosaic",
      rfrr: "mosaic.gallery.open",
      action: Action.CLICK,
      linkName: "RFRR Action Event",
    };
    sendImmediateTrackEvent(trackingInfo, analytics);

    setCurrentMediaGalleryIndex(index);

    if (routeStore) {
      routeStore.updateSearch({ gallery: "open" });
    }
  };

  const openThumbnailsGallery = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryMosaicThumbnails",
      rfrr: "mosaic.gallery.thumbnails.open",
      action: Action.CLICK,
      linkName: "RFRR Action Event",
    };
    sendImmediateTrackEvent(trackingInfo, analytics);
    if (routeStore) {
      routeStore.updateSearch({ thumbnails: "open" });
    }
  };

  const closeMediaGallery = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryMosaic",
      rfrr: "mosaic.gallery.close",
      action: Action.CLICK,
      linkName: "RFRR Action Event",
    };
    sendImmediateTrackEvent(trackingInfo, analytics);

    if (routeStore) {
      routeStore.closeDialog("gallery");
    }
  };

  const closeThumbnailsGallery = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: "GalleryMosaicThumbnails",
      rfrr: "mosaic.gallery.thumbnails.close",
      action: Action.CLICK,
      linkName: "RFRR Action Event",
    };
    sendImmediateTrackEvent(trackingInfo, analytics);
    if (routeStore) {
      routeStore.closeDialog("thumbnails");
    }
  };

  let thumbIndex = 0;
  const imageKeyHelper: ItemKeyHelper = new ItemKeyHelper("mosaic-image");

  const heroThumb = images && images.length && (
    <DestinationGalleryMosaicThumb
      key={imageKeyHelper.next()}
      position={thumbIndex}
      image={images[0]}
      onImageSelected={openMediaGallery}
      singleImage={images.length === 1}
    />
  );

  let thumbs: JSX.Element[];
  if (images && images.length) {
    thumbs = images.slice(1, 5).map((image: GalleryImage) => {
      thumbIndex++;

      return (
        <DestinationGalleryMosaicThumb
          key={imageKeyHelper.next()}
          position={thumbIndex}
          image={image}
          onImageSelected={openMediaGallery}
        />
      );
    });
  } else {
    thumbs = [];
  }

  return (
    <div>
      <QueryRoute query={{ gallery: "open" }}>
        <Layer id="destination-image-mosaic" onDismiss={closeMediaGallery}>
          <UitkMediaGalleryImageDialog
            animation={UitkDialogAnimationType.NONE}
            dialogShow={true}
            strictFullscreen={true}
            onDismiss={closeMediaGallery}
          >
            <UitkMediaGalleryImageView
              images={galleryImages}
              currentIndex={currentMediaGalleryIndex}
              onIndexChange={setDialogGalleryIndex}
            />
          </UitkMediaGalleryImageDialog>
        </Layer>
      </QueryRoute>

      <QueryRoute query={{ thumbnails: "open" }}>
        <Layer id="destination-image-thumbnails" onDismiss={closeThumbnailsGallery}>
          <UitkFullscreenDialog
            strictFullscreen={false}
            dialogShow={false}
            animation={UitkDialogAnimationType.SLIDEUP}
            className="uitk-dialog-fullscreen-bg-default"
          >
            <UitkDialogContent className="no-padding">
              <UitkToolbar
                header={props.title}
                iconLabel={formatText("destinationThumbnailsGallery.button.close")}
                action={closeThumbnailsGallery}
              />

              <UitkMediaGalleryThumbnailView images={props.images} onImageSelected={openMediaGallery} />
            </UitkDialogContent>
          </UitkFullscreenDialog>
        </Layer>
      </QueryRoute>

      <div className={`GalleryMosaic GalleryMosaic--${thumbs.length + 1}`}>
        <div className="galleryMosaicHero">{heroThumb}</div>

        <div className="galleryMosaicThumbs" aria-hidden={true}>
          {thumbs}
        </div>

        <UitkScrim className="galleryMosaicScrim" type={UitkScrimType.BOTTOM}>
          <UitkButton onClick={openThumbnailsGallery} className="galleryMosaicButton">
            <span aria-hidden="true">{props.images.length}</span>
            <UitkIcon
              name="photo_library"
              title={formatText("galleryMosaic.seeAllTitle", props.images.length)}
              id="photo_library"
              shadow={true}
            />
          </UitkButton>
        </UitkScrim>
      </div>
    </div>
  );
});

const Attribution = observer((attributionProps: AttributionProps) => {
  const { image } = attributionProps;

  if (!image.source) {
    return null;
  }

  return (
    <AttributionText
      source={image.source}
      origin={image.origin}
      attribUrl={image.attribUrl}
      authorName={image.authorName}
      authorUrl={image.authorUrl}
      licenseSuite={image.licenseSuite}
      licenseName={image.licenseName}
      licenseUrl={image.licenseUrl}
    />
  );
});

export const DestinationGalleryMosaicThumb = observer((props: GalleryMosaicThumbProps) => {
  const { image, singleImage } = props;

  const onImageSelected = () => {
    props.onImageSelected(props.position);
  };

  return (
    <div className="galleryMosaicThumb">
      <LazyLoad
        placeholder={
          <div onClick={onImageSelected} data-testid={`galleryMosaicThumb-${props.position}`}>
            <UitkPlaceholderCard />
          </div>
        }
      >
        <UitkFigure ratio={singleImage ? UitkFigureAspectRatioType.R21_9 : UitkFigureAspectRatioType.R16_9}>
          <div onClick={onImageSelected} data-testid={`galleryMosaicThumb-${props.position}`}>
            <UitkImage src={image.src} alt={image.alt} placeholderImage={false} />
          </div>
          <Attribution image={image} />
        </UitkFigure>
      </LazyLoad>
    </div>
  );
});

export default GalleryMosaic;
